import React, { useState } from 'react';
import styled from 'styled-components';
import { useCart } from '../../contexts/CartContext';
import CheckoutButtons from './CheckoutButtons';

// Format price with currency
const formatPrice = (price, currency = 'USD') => {
  if (price === null || price === undefined) return 'Price not available';
  if (price === 0) return 'Free';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(price);
};

const CheckoutContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
`;

const SellerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
`;

const SellerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SellerAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 1.2rem;
`;

const SellerDetails = styled.div`
  h3 {
    margin: 0;
    color: #1f2937;
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    color: #6b7280;
    font-size: 0.9rem;
  }
`;

const ItemCount = styled.div`
  background: #f3f4f6;
  color: #374151;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const ItemsList = styled.div`
  margin-bottom: 1.5rem;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const ItemImage = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 1.5rem;
`;

const ItemDetails = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 0.25rem 0;
    color: #1f2937;
    font-size: 0.95rem;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    color: #6b7280;
    font-size: 0.85rem;
  }
`;

const ItemPrice = styled.div`
  text-align: right;
  
  .quantity {
    color: #6b7280;
    font-size: 0.85rem;
    margin-bottom: 0.25rem;
  }
  
  .total {
    color: #1f2937;
    font-weight: 600;
    font-size: 1rem;
  }
`;

const TotalSection = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
    font-weight: 700;
    font-size: 1.1rem;
    color: #1f2937;
    border-top: 1px solid #e5e7eb;
    padding-top: 0.5rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 0.9rem;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

const SaveForLaterButton = styled(ActionButton)`
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const RemoveButton = styled(ActionButton)`
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  
  &:hover {
    background: #fee2e2;
  }
`;

const MultiSellerCheckout = ({ onCheckoutComplete, onRequireLogin }) => {
  const { getSellerGroups, removeItemsBySeller } = useCart();
  const [savedSellers, setSavedSellers] = useState(new Set());

  const sellerGroups = getSellerGroups();

  const handleSaveForLater = (sellerId) => {
    setSavedSellers(prev => new Set([...prev, sellerId]));
    // In a real implementation, you might want to save these to a "saved for later" list
    console.log('Saved items for later from seller:', sellerId);
  };

  const handleRemoveSeller = (sellerId) => {
    if (window.confirm('Are you sure you want to remove all items from this seller?')) {
      removeItemsBySeller(sellerId);
    }
  };

  const handleCheckoutComplete = (sellerId) => {
    // Remove items from this seller after successful checkout
    removeItemsBySeller(sellerId);
    if (onCheckoutComplete) {
      onCheckoutComplete(sellerId);
    }
  };

  if (sellerGroups.length === 0) {
    return (
      <CheckoutContainer>
        <p style={{ textAlign: 'center', color: '#6b7280' }}>
          Your cart is empty
        </p>
      </CheckoutContainer>
    );
  }

  return (
    <div>
      {sellerGroups.map((sellerGroup) => {
        const isSaved = savedSellers.has(sellerGroup.sellerId);
        
        return (
          <CheckoutContainer key={sellerGroup.sellerId}>
            <SellerHeader>
              <SellerInfo>
                <SellerAvatar>
                  {sellerGroup.sellerName.charAt(0).toUpperCase()}
                </SellerAvatar>
                <SellerDetails>
                  <h3>{sellerGroup.sellerName}</h3>
                  <p>Seller</p>
                </SellerDetails>
              </SellerInfo>
              <ItemCount>
                {sellerGroup.totalItems} item{sellerGroup.totalItems !== 1 ? 's' : ''}
              </ItemCount>
            </SellerHeader>

            <ItemsList>
              {sellerGroup.items.map((item) => (
                <ItemRow key={item.productId}>
                  <ItemInfo>
                    <ItemImage>
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.productName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
                        />
                      ) : (
                        '📦'
                      )}
                    </ItemImage>
                    <ItemDetails>
                      <h4>{item.productName}</h4>
                      <p>{formatPrice(item.price, item.currency || 'USD')} each</p>
                    </ItemDetails>
                  </ItemInfo>
                  <ItemPrice>
                    <div className="quantity">Qty: {item.quantity}</div>
                    <div className="total">{formatPrice(item.price * item.quantity, item.currency || 'USD')}</div>
                  </ItemPrice>
                </ItemRow>
              ))}
            </ItemsList>

            <TotalSection>
              <TotalRow>
                <span>Subtotal:</span>
                <span>
                  {formatPrice(sellerGroup.totalPrice, sellerGroup.items.length > 0 ? (sellerGroup.items[0].currency || 'USD') : 'USD')}
                </span>
              </TotalRow>
              <TotalRow>
                <span>Total:</span>
                <span>
                  {formatPrice(sellerGroup.totalPrice, sellerGroup.items.length > 0 ? (sellerGroup.items[0].currency || 'USD') : 'USD')}
                </span>
              </TotalRow>
            </TotalSection>

            {isSaved ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: '#059669', background: '#d1fae5', borderRadius: '8px' }}>
                ✅ Saved for later
              </div>
            ) : (
              <>
                <CheckoutButtons 
                  items={sellerGroup.items} 
                  onCheckout={() => handleCheckoutComplete(sellerGroup.sellerId)}
                  onRequireLogin={onRequireLogin}
                />
                
                <ActionButtons>
                  <SaveForLaterButton onClick={() => handleSaveForLater(sellerGroup.sellerId)}>
                    💾 Save for Later
                  </SaveForLaterButton>
                  <RemoveButton onClick={() => handleRemoveSeller(sellerGroup.sellerId)}>
                    🗑️ Remove All
                  </RemoveButton>
                </ActionButtons>
              </>
            )}
          </CheckoutContainer>
        );
      })}
    </div>
  );
};

export default MultiSellerCheckout;
